import React from 'react';
import { Link } from 'react-router-dom';
import { FaFileWord, FaDownload, FaTrophy, FaMedal, FaAward, FaEnvelope, FaExternalLinkAlt } from 'react-icons/fa';
import './PrizeBestPaperPage.css';

const medals = [
  'Phillip Gould Medal',
  'Herbert Mang Medal',
  'Sritawat Kitipornchai Medal',
  'Engineering Structures Medal',
];

const regions = [
  'Asia-Pacific and the Middle East',
  'Europe',
  'The Americas, Africa, and the Rest of the World',
];

const submissionDocs = [
  'Nomination Form (can be downloaded below)',
  'Nominee\'s CV',
  'Nominee\'s Full Publication List',
  'Statement of Impact',
  'Two Referee Letters from Full Professor',
];

const downloadForms = [
  {
    name: 'ICES Medal and Prize Nomination Form',
    file: '/templates/ICES2026_Nomination_Form.docx',
    format: '.docx',
  },
];

const PrizeBestPaperPage = () => {
  return (
    <section className="prize-page fade-in-up">
      <div className="prize-wrapper">
        <span className="badge prize-badge">Awards</span>
        <h1 className="prize-heading">Medal and Prize Nominations</h1>
        <p className="prize-subtitle">
          The Medals and Prizes of the journal <strong>Engineering Structures</strong> recognise individuals whose work exemplifies the highest standards of excellence in the areas covered by the journal Engineering Structures. These awards celebrate both internationally recognised senior leaders and exceptional mid- and early-career researchers who have made significant contributions to advancing structural engineering research and practice.
        </p>

        {/* Medals Section */}
        <div className="prize-section">
          <div className="prize-section-header">
            <div className="prize-section-icon medal-icon">
              <FaMedal />
            </div>
            <h2 className="prize-section-title">Medals</h2>
          </div>
          <p className="prize-section-text">
            Four medals will be awarded at each ICES conference. Three of the medals are named in honour of the Founding Editor, Prof Phillip Gould, and Honorary Editors, Prof Herbert Mang and Prof Sritawat Kitipornchai, in recognition of their outstanding efforts, achievements, and contributions to the fields covered by the journal Engineering Structures.
          </p>
          <p className="prize-section-text">
            These medals are intended for senior eminent researchers or engineers whose careers are distinguished by exceptional, sustained, and internationally recognised contributions to the fields covered by the journal Engineering Structures. Nominees should have demonstrated authoritative scholarship, transformative innovation, or pioneering professional practice with lasting global impact.
          </p>
          <div className="prize-section-subheading">The four medals are:</div>
          <div className="prize-items-list">
            {medals.map((medal, index) => (
              <div className="prize-item" key={index}>
                <div className="prize-item-icon">
                  <FaTrophy />
                </div>
                <span>{medal}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Prizes Section */}
        <div className="prize-section">
          <div className="prize-section-header">
            <div className="prize-section-icon award-icon">
              <FaAward />
            </div>
            <h2 className="prize-section-title">Prizes</h2>
          </div>
          <p className="prize-section-text">
            Four Engineering Structures Prizes will be awarded at each ICES conference. These prizes recognise mid- and early-career researchers or engineers who have achieved outstanding scholarly or professional accomplishments and demonstrate clear evidence of internationally recognised excellence. Nominees for a Prize must be under 45 years of age as of 1st January the calendar year of the award. Eligibility is determined strictly based on the award year.
          </p>
          <p className="prize-section-text">
            The Engineering Structures Prizes will also consider global representation. Accordingly, awards will reflect the nominee's institutional affiliation at the time of nomination across the following regions:
          </p>
          <div className="prize-regions">
            {regions.map((region, index) => (
              <div className="prize-region-chip" key={index}>
                {region}
              </div>
            ))}
          </div>
        </div>

        {/* Nomination and Selection */}
        <div className="prize-section">
          <div className="prize-section-header">
            <div className="prize-section-icon selection-icon">
              <FaTrophy />
            </div>
            <h2 className="prize-section-title">Nomination and Selection of Medal and Prizes</h2>
          </div>
          <p className="prize-section-text">
            To ensure a rigorous nomination process and uphold the highest standards, nominations are invited only from individuals in leadership positions who have a broad view of the field's progress. They are:
          </p>
          <ul className="prize-nominators-list">
            <li>
              All Editorial Board members of journal Engineering Structures (a list of the Editorial Board members can be found at{' '}
              <a href="https://www.sciencedirect.com/journal/engineering-structures/about/editorial-board" target="_blank" rel="noopener noreferrer">
                Editorial Board <FaExternalLinkAlt style={{ fontSize: '0.65rem', marginLeft: '4px' }} />
              </a>)
            </li>
            <li>
              The Chairs, Vice Chairs, and Advisory Committee members of the ICES Conference in the year of the award (a list of these conference committee members can be found at{' '}
              <Link to="/committees/co-chairs" target="_blank" rel="noopener noreferrer">
                ices2026.com <FaExternalLinkAlt style={{ fontSize: '0.65rem', marginLeft: '4px' }} />
              </Link>)
            </li>
          </ul>
          <p className="prize-section-text">
            Each nominator can only submit one nomination for a medal and one nomination for a prize.
          </p>
          <p className="prize-section-text">
            Nominations should be based on the nominee's demonstrated excellence and impact within the fields covered by journal Engineering Structures. In evaluating candidates, special attention will be given to the originality, depth, and influence of their sustained contributions; the extent to which their work has advanced fundamental understanding, methodological development, or engineering practice; the degree of international recognition and professional leadership; and their sustained contributions to Engineering Structures journal. Emphasis is placed on sustained achievement and enduring influence over the course of a distinguished career.
          </p>
          <p className="prize-section-text">
            An Award Committee will be established, and a two-stage selection and voting process will be undertaken to determine the awardees.
          </p>
        </div>

        {/* Submission Details */}
        <div className="prize-section">
          <div className="prize-section-header">
            <div className="prize-section-icon submission-icon">
              <FaEnvelope />
            </div>
            <h2 className="prize-section-title">Submission Details</h2>
          </div>
          <div className="prize-deadline-card">
            <div className="prize-deadline-label">Submission Deadline</div>
            <div className="prize-deadline-date">July 15, 2026</div>
            <div className="prize-deadline-email">
              Submit via email to{' '}
              <a href="mailto:ices2026@uq.edu.au">ices2026@uq.edu.au</a>
            </div>
          </div>
          <div className="prize-section-subheading">The nomination must consist of the following documents:</div>
          <div className="prize-submission-docs">
            {submissionDocs.map((doc, index) => (
              <div className="prize-doc-item" key={index}>
                <div className="prize-doc-number">{index + 1}</div>
                <span>{doc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Attendance Note */}
        <div className="prize-note">
          <p>
            The awardee must attend the ICES conference in which the Medal and Prizes are presented. Following the conference, awardees will be interviewed and featured in Engineering Structures journal's social media platforms. Awardees are also expected to deliver a presentation in Engineering Structures Research Forums.
          </p>
        </div>

        {/* Download Forms */}
        <h2 className="prize-downloads-heading">Download Nomination Forms</h2>
        <p className="prize-downloads-subtitle">All forms are in Microsoft Word (.docx) format.</p>
        <div className="prize-downloads-grid">
          {downloadForms.map((form) => (
            <a
              key={form.name}
              href={form.file}
              download
              className="download-card"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="download-icon-wrapper">
                <FaFileWord />
              </div>
              <div className="download-card-text">
                <div className="download-card-title">{form.name}</div>
                <div className="download-card-format">{form.format}</div>
              </div>
              <FaDownload className="download-arrow" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PrizeBestPaperPage;
